import React, { useState, useEffect, useRef } from 'react';
import { collegeJourneyService } from '../../lib/college-journey-service';
import { Search, Building2, MapPin, X, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';

const CollegeSelector = ({ 
  onCollegeSelect, 
  onRequestNewCollege,
  placeholder = "Search for a college...",
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Load colleges on mount
  useEffect(() => {
    loadColleges();
  }, []);

  // Filter colleges based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredColleges(colleges.slice(0, 10)); // Show first 10 by default
      return;
    }

    const filtered = colleges.filter(college =>
      college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      college.official_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      college.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      college.state?.toLowerCase().includes(searchTerm.toLowerCase())
    ).slice(0, 20); // Limit to 20 results

    setFilteredColleges(filtered);
  }, [searchTerm, colleges]);

  const loadColleges = async () => {
    try {
      setLoading(true);
      const data = await collegeJourneyService.getAvailableColleges('', 500); // Load more colleges initially
      setColleges(data);
      setFilteredColleges(data.slice(0, 10));
    } catch (error) {
      console.error('Error loading colleges:', error);
      toast.error('Failed to load colleges');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputBlur = () => {
    // Delay closing to allow for click events
    setTimeout(() => setIsOpen(false), 200);
  };

  const handleCollegeSelect = (college) => {
    setSearchTerm(college.name);
    setIsOpen(false);
    onCollegeSelect(college);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredColleges.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredColleges.length) {
          handleCollegeSelect(filteredColleges[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const handleRequestNew = () => {
    if (onRequestNewCollege) {
      onRequestNewCollege(searchTerm);
    }
    setIsOpen(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-stone-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="block w-full pl-10 pr-10 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500 bg-white text-sm"
        />
        
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-4 w-4 text-stone-400 hover:text-slate-600" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-stone-500 text-center">
              Loading colleges...
            </div>
          ) : filteredColleges.length > 0 ? (
            <>
              {filteredColleges.map((college, index) => (
                <div
                  key={college.id}
                  onClick={() => handleCollegeSelect(college)}
                  className={`px-4 py-3 cursor-pointer flex items-start space-x-3 hover:bg-stone-50 ${
                    index === selectedIndex ? 'bg-azure-50' : ''
                  }`}
                >
                  <Building2 className="h-4 w-4 text-stone-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-grow min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {college.name}
                    </div>
                    {college.official_name && college.official_name !== college.name && (
                      <div className="text-xs text-stone-500 truncate">
                        {college.official_name}
                      </div>
                    )}
                    {(college.city || college.state) && (
                      <div className="text-xs text-stone-400 flex items-center mt-1">
                        <MapPin className="h-3 w-3 mr-1" />
                        {[college.city, college.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Request new college option */}
              {searchTerm.trim() && filteredColleges.length < 5 && onRequestNewCollege && (
                <div
                  onClick={handleRequestNew}
                  className="px-4 py-3 cursor-pointer flex items-center space-x-3 hover:bg-stone-50 border-t border-stone-100"
                >
                  <Plus className="h-4 w-4 text-azure-500 flex-shrink-0" />
                  <div className="text-sm text-azure-600">
                    Request to add "{searchTerm}"
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="px-4 py-3">
              <div className="text-sm text-stone-500 text-center mb-2">
                No colleges found matching "{searchTerm}"
              </div>
              {onRequestNewCollege && searchTerm.trim() && (
                <button
                  onClick={handleRequestNew}
                  className="w-full px-3 py-2 text-sm bg-azure-50 text-azure-600 rounded hover:bg-azure-100 flex items-center justify-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Request to add "{searchTerm}"</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollegeSelector;